import React from "react";
import { Alert } from "antd";

type Props = {
    children: React.ReactNode;
    fallback?: React.ReactNode;
};

type State = {
    hasError: boolean;
};

/**
 * Contains a render error to the subtree it happened in, so the surrounding page keeps
 * working. That matters where the page owns the only control that can repair the bad data
 * it just choked on - without a boundary, React unmounts that control along with everything
 * else and the user is left with no way forward.
 *
 * The error is still reported to the console, so it stays visible in DevTools and to any
 * error reporting that listens there.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error("Render error contained by ErrorBoundary:", error, errorInfo);
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.props.fallback !== undefined) {
            return this.props.fallback;
        }

        return (
            <Alert
                message="This section could not be displayed"
                description="Something went wrong while rendering this section. The rest of the page is unaffected."
                type="warning"
                showIcon
            />
        );
    }
}
